import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, CircularProgress, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import moment from 'moment'
import 'moment/locale/fr'
import { isNil, orderBy, uniqBy } from 'lodash'
import ApplicationConf from 'conf/ApplicationConf'
import useFetch from 'common/hooks/useFetch'
import { CardTitle } from 'common/components/card/Card'
import { darkBlue } from 'app/theme'
import { STATUS } from 'common/constants/storeConstants'

const SLA_TARGET = 99.5

// « Taux Client > 99.5% » est une part de clients, pas une disponibilité : pas de couleur selon l'objectif.
const RATIO_LABEL_PREFIX = 'Taux'

const STATS = [
    { key: 'tauxOuvre', title: 'Taux heure ouvrée' },
    { key: 'tauxOuvrable', title: 'Taux heure ouvrable' },
]

const formatRate = (rate) => (isNil(rate) ? '—' : `${Number(rate).toFixed(2)} %`)

const formatMonth = ({ annee, mois }) => {
    const label = moment({ year: annee, month: mois - 1 }).locale('fr').format('MMMM')
    return `${label.charAt(0).toUpperCase()}${label.slice(1)}`
}

const rateColor = (rate, isColored) => {
    if (isNil(rate)) {
        return '#98a3b8'
    }
    if (!isColored) {
        return '#1c2340'
    }
    return rate >= SLA_TARGET ? '#2e7d32' : '#c62828'
}

const cellSx = { borderBottom: '1px solid #e6eaf2', fontSize: 14 }
const headSx = { ...cellSx, borderBottom: '1px solid #cfd6e4', fontWeight: 700, color: '#1c2340', backgroundColor: '#f5f7fa' }

// Une card par statistique (heure ouvrée / ouvrable) : une ligne par label, une colonne par mois.
const SlaCard = ({ title, statKey, months, labels, getRow }) => (
    <Card sx={{ height: '100%' }}>
        <CardTitle title={title} color={darkBlue} />
        <CardContent>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell sx={headSx} />
                        {months.map(m => (
                            <TableCell key={`${m.annee}-${m.mois}`} align='right' sx={headSx}>{formatMonth(m)}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {labels.map(label => (
                        <TableRow key={label} hover>
                            <TableCell sx={{ ...cellSx, fontWeight: 600, color: '#1c2340' }}>{label}</TableCell>
                            {months.map(m => {
                                const rate = getRow(label, m)?.[statKey]
                                return (
                                    <TableCell
                                        key={`${m.annee}-${m.mois}`}
                                        align='right'
                                        sx={{ ...cellSx, color: rateColor(rate, !label.startsWith(RATIO_LABEL_PREFIX)) }}
                                    >
                                        {formatRate(rate)}
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </CardContent>
    </Card>
)
SlaCard.propTypes = {
    title: PropTypes.string,
    statKey: PropTypes.string,
    months: PropTypes.arrayOf(PropTypes.shape({ annee: PropTypes.number, mois: PropTypes.number })),
    labels: PropTypes.arrayOf(PropTypes.string),
    getRow: PropTypes.func,
}

// Taux de disponibilité des 3 derniers mois (table uptime_sla).
const UptimeSlaCard = () => {
    const { data = [], status } = useFetch(ApplicationConf.supervision.uptimeSla())

    const months = useMemo(
        () => orderBy(uniqBy(data.map(({ annee, mois }) => ({ annee, mois })), m => `${m.annee}-${m.mois}`), ['annee', 'mois'], ['asc', 'asc']),
        [data],
    )
    const labels = useMemo(() => uniqBy(data, 'label').map(d => d.label), [data])

    const getRow = (label, month) => data.find(d => d.label === label && d.annee === month.annee && d.mois === month.mois)

    return (
        <>
            {status === STATUS.LOADING && data.length === 0 && <CircularProgress size={24} />}
            {status === STATUS.SUCCEEDED && data.length === 0 && <Typography sx={{ fontSize: 13, color: '#98a3b8' }}>Aucune donnée</Typography>}
            {data.length > 0 && (
                <Grid container spacing={2}>
                    {STATS.map(({ key, title }) => (
                        <Grid key={key} size={{ xs: 12, md: 6 }}>
                            <SlaCard title={title} statKey={key} months={months} labels={labels} getRow={getRow} />
                        </Grid>
                    ))}
                </Grid>
            )}
        </>
    )
}

export default UptimeSlaCard
